import {
  Button,
  ButtonGroup,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useDrawerStateDispatch } from 'features/main/context';
import { pdfjs, Document, Page } from 'react-pdf';
import '../assets/AnnotationLayer.css';
import '../assets/TextLayer.css';
import { constructConnectPayload, ForwardedModalBody } from '../utils/utils';
import { useEndOfText } from '../hooks/useEndOfText';
import { getPdfWorkerSrc } from './pdfWorkerSrc';
import { t } from '@transifex/native';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import {
  GetOrderActivationsDocument,
  useOrderActivationConnectMutation,
} from 'gql/graphql';

pdfjs.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc();

export const ConnectTermsModal = ({
  isOpen,
  onCloseHandler,
}: {
  isOpen: boolean;
  onCloseHandler: (value: boolean) => void;
}) => {
  const toast = useToast();
  const [, drawerDispatch] = useDrawerStateDispatch();

  const [orderActivationConnectMutation, { loading }] =
    useOrderActivationConnectMutation();

  const { isEndOfText, setIsEndOfText, node } = useEndOfText();

  const modalCloseDrawer = () => {
    setIsEndOfText(false);
    onCloseHandler(false);
  };

  const handleSubmit = async () => {
    const variables = constructConnectPayload();

    try {
      const { data } = await orderActivationConnectMutation({
        variables,
        refetchQueries: [GetOrderActivationsDocument],
      });

      const error = data?.orderActivation.errors?.[0];
      if (error) {
        return toast({
          description: error?.message,
          title: 'Error',
          status: 'error',
        });
      }

      drawerDispatch(closeDrawer());
      onCloseHandler(false);
      toast({
        title: t(
          'You will be notified via a welcome email when you can access the Connect app.',
        ),
        status: 'success',
      });
    } catch (error: any) {
      const { networkError } = error ?? {};
      toast({
        description: networkError.message,
        title: `Error ${networkError.statusCode}`,
        status: 'error',
      });
    }
  };

  return (
    <Modal
      onClose={modalCloseDrawer}
      isOpen={isOpen}
      size='4xl'
      scrollBehavior='inside'
      blockScrollOnMount={false}
    >
      <ModalOverlay />
      <ModalContent aria-label='trial consent'>
        <ModalHeader>Connect Terms and Conditions</ModalHeader>
        <ModalCloseButton />
        <ForwardedModalBody aria-label='consent body' ref={node}>
          <Document
            externalLinkTarget='_blank'
            file='/portal-ui/Sitecore Connect Trial EULA.pdf'
          >
            <Page pageNumber={1} />
            <br />
            <Page pageNumber={2} />
            <br />
            <Page pageNumber={3} />
            <br />
            <Page pageNumber={4} />
            <br />
          </Document>
        </ForwardedModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button
              data-testid='cancel-btn'
              type='button'
              variant='ghost'
              onClick={modalCloseDrawer}
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant='solid'
              onClick={handleSubmit}
              isDisabled={!isEndOfText}
              isLoading={loading}
              data-testid='connect-accept-enable'
              aria-label='accept and enable'
            >
              Accept and enable
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
